import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Button from 'components/Button';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {AlertDialog} from 'components/dialogs/AlertDialog';
import React from 'react';
import {Trans} from 'react-i18next';
import {useSelector} from 'react-redux';
import {buildNumber, versionNumber} from 'common/constants';
import Panel from 'components/Panel';
import i18n from 'i18n';
import {ReactComponent as Logo} from 'images/cs-logo.svg';
import selectors from 'redux/license/selectors';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {enums} from 'types/enums';
import {formatDateOrNever} from 'common/dateUtils';
import {loginSelectors} from 'features/login/redux';

type HelpAboutBoxProps = {close: () => void};

const useStyles = makeStyles((theme) => ({
   root: {
      padding: theme.spacing(2),
   },
   pushedRight: {
      textAlign: 'right',
   },
   centered: {
      textAlign: 'center',
   },
}));

const HelpAboutBox = ({close}: HelpAboutBoxProps) => {
   const classes = useStyles();
   const [isExportOpened, setExportOpened] = React.useState(false);
   const openExportDialog = () => setExportOpened(true);
   const closeExportDialog = () => setExportOpened(false);
   const license = useSelector(selectors.getLicense);
   const machineName = useSelector(loginSelectors.machineName);

   return (
      <Grid className={classes.root} container spacing={2}>
         <Grid item xs={9}>
            <Typography gutterBottom>
               <Trans>
                  Diplomat Managed File Transfer {enums.LicenseEdition[license.edition]} Edition
               </Trans>
            </Typography>
            <Typography gutterBottom>
               <Trans>Copyright &copy; 2004-2025 Coviant Software LLC. All Rights Reserved.</Trans>
            </Typography>
         </Grid>
         <Grid className={classes.pushedRight} item xs={3}>
            <Logo />
         </Grid>
         <Grid item xs={12}>
            <Panel name="Licensed To">
               <Typography>
                  <Trans>{license.licenser}</Trans>
               </Typography>
               <Typography>
                  <Trans>License ID: {license.serialNumber}</Trans>
               </Typography>
               <Typography>
                  <Trans>Expires: {formatDateOrNever(license.expires)}</Trans>
               </Typography>
               <Typography>
                  <Trans>Machine Name: {machineName}</Trans>
               </Typography>
            </Panel>
         </Grid>
         <Grid className={classes.pushedRight} item xs={12}>
            <Typography gutterBottom>
               Version {versionNumber} (Build {buildNumber})
            </Typography>
            <PrimaryButton className="button" onClick={openExportDialog}>
               <Trans>Export Restrictions</Trans>
            </PrimaryButton>
         </Grid>
         <Grid className={classes.centered} item xs={12}>
            <Button onClick={close}>
               <Trans>Ok</Trans>
            </Button>
         </Grid>
         {isExportOpened ? (
            <AlertDialog
               title={i18n.t('Diplomat Export Restrictions')}
               content={
                  <>
                     <Typography>
                        <Trans>
                           Diplomat products may NOT be downloaded or otherwise exported or
                           re-exported to any parties in Cuba, Iran, Libya, North Korea, Sudan, or
                           Syria. You agree not to directly or indirectly export or re-export
                           (including by transmission) these Diplomat products to any parties in the
                           above countries without first obtaining any required export license or
                           governmental approval.
                        </Trans>
                     </Typography>
                     <br />
                     <br />
                     <Typography>
                        <Trans>
                           By downloading or using Diplomat products, you are agreeing to the
                           foregoing and you are representing and warranting that you are not
                           located in and are not a national or resident of Cuba, Iran, Libya, North
                           Korea, Sudan, or Syria.
                        </Trans>
                     </Typography>
                     <br />
                     <br />
                     <Typography>
                        <Trans>
                           DIPLOMAT PRODUCTS CONTAIN ENCRYPTION TECHNOLOGY THAT IS CONTROLLED FOR
                           EXPORT BY THE U.S. BUREAU OF INDUSTRY AND SECURITY UNDER THE EXPORT
                           ADMINISTRATION REGULATIONS. IN ADDITION TO OTHER RESTRICTIONS DESCRIBED
                           IN THIS DOCUMENT AND THE DIPLOMAT LICENSE AGREEMENT, YOU MAY NOT USE
                           DIPLOMAT PRODUCTS, OR EXPORT DIPLOMAT PRODUCTS TO ANY PARTY WHERE YOU
                           KNOW, OR HAVE GOOD REASON TO BELIEVE, THAT DIPLOMAT PRODUCTS MAY BE USED
                           IN CONNECTION WITH THE PROLIFERATION OF NUCLEAR, CHEMICAL OR BIOLOGICAL
                           WEAPONS OR MISSILES.
                        </Trans>
                     </Typography>
                     <br />
                     <br />
                     <Typography>
                        <Trans>
                           Diplomat products are classified under ECCN 5D992B.1 with CCATS # G049200
                           as of June 14, 2006 which authorizes these products for export and
                           re-export under Section 742.15 (B) (2) of the Export Administration
                           Regulations ("Review Requirement for Mass Market Encryption Commodities
                           and Software Exceeding 64 Bits").
                        </Trans>
                     </Typography>
                  </>
               }
               onClose={closeExportDialog}
            />
         ) : null}
      </Grid>
   );
};

type Props = {
   onClose: () => void;
};
export const HelpAboutDialog = ({onClose}: Props) => {
   return (
      <Dialog maxWidth="md" header={DialogTypes.HelpAboutBox.dialogName} close={onClose}>
         <HelpAboutBox close={onClose} />
      </Dialog>
   );
};

export default React.memo(HelpAboutDialog);
